import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button, { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useTutorial } from "@rapid/hooks/useTutorial";
import { AutoAdvanceDots } from "@rapid/components/tutorial/AutoAdvanceDots";
import { useKeyboardShortcut } from "@rapid/hooks/useKeyboardShortcut";
import {
    useDraggableCard,
    DRAGGABLE_CARD_ANIMATION,
    DRAGGABLE_CARD_CLASSES,
    DEFAULT_CARD_WIDTH,
    clampPositionToViewport
} from "@rapid/hooks/useDraggableCard";
import { STEPS } from "@rapid/components/tutorial/tutorialSteps";
import { TooltipContainer } from "@scripts/react/components/Tooltip.jsx";

export interface TutorialStep {
    icon: string;
    title: string;
    description: string;
    videoSrc?: string;
}

interface TutorialCardProps {
    /** Inline mode - embedded in a container */
    inline?: boolean;
    /** Standalone mode - controlled externally with isOpen/onClose, doesn't use tutorial context */
    standalone?: boolean;
    /** Whether the standalone card is open */
    isOpen?: boolean;
    /** Callback when standalone card is closed */
    onClose?: () => void;
    /** Custom steps to display instead of default tutorial steps */
    steps?: TutorialStep[];
    /** Initial position for standalone mode */
    initialPosition?: { left: number; top: number } | "bottom-right";
    /** Auto-advance delay in ms for icon-only steps (no video) */
    autoAdvanceDelay?: number;
    /** Auto-play videos (defaults to true for inline mode) */
    autoPlay?: boolean;
}

// Floating card constants
const NAVBAR_HEIGHT = 48;
const LEFT_OFFSET = 8;
const TOP_OFFSET = NAVBAR_HEIGHT + LEFT_OFFSET;
const CARD_HEIGHT = 360;
const DEFAULT_INITIAL_POSITION = { left: LEFT_OFFSET, top: TOP_OFFSET };

export function TutorialCard({
    inline = false,
    standalone = false,
    isOpen: standaloneIsOpen,
    onClose,
    steps: customSteps,
    initialPosition = DEFAULT_INITIAL_POSITION,
    autoAdvanceDelay = 5000,
    autoPlay
}: TutorialCardProps) {
    // For inline mode, manage state locally; for floating mode, use context (unless standalone)
    const tutorialContext = useTutorial();
    const [localStep, setLocalStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const shouldAutoPlayRef = useRef(true);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Use custom steps if provided, otherwise default tutorial steps
    const steps = customSteps || STEPS;

    // Determine if this step has a video
    const hasVideo = (step: TutorialStep) => !!step.videoSrc;

    // Use local state for inline/standalone, context for floating
    const currentStep = inline || standalone ? localStep : tutorialContext.currentStep;
    const goTo = inline || standalone ? setLocalStep : tutorialContext.goTo;
    const step = steps[currentStep];

    // Determine visibility
    const isVisible = standalone ? standaloneIsOpen : !tutorialContext.isCardDismissed;

    const getBottomRightPosition = useCallback(() => {
        if (typeof window === "undefined") {
            return DEFAULT_INITIAL_POSITION;
        }

        // Calculate desired position
        const desiredLeft = window.innerWidth - DEFAULT_CARD_WIDTH - 40;
        const desiredTop = window.innerHeight - CARD_HEIGHT - 120;

        // Clamp to viewport bounds using shared utility
        return clampPositionToViewport({
            position: { left: desiredLeft, top: desiredTop },
            cardWidth: DEFAULT_CARD_WIDTH,
            cardHeight: CARD_HEIGHT,
            minLeft: LEFT_OFFSET,
            minTop: TOP_OFFSET,
            minBottom: LEFT_OFFSET
        });
    }, []);

    const getInitialPosition = useCallback(() => {
        if (initialPosition === "bottom-right") {
            return getBottomRightPosition();
        }
        return initialPosition;
    }, [initialPosition, getBottomRightPosition]);

    const prevDismissedRef = useRef<boolean>(tutorialContext.isCardDismissed);

    const { position, setPosition, cardRef, startDrag } = useDraggableCard({
        isOpen: Boolean(isVisible && !inline),
        cardHeight: CARD_HEIGHT,
        minLeft: LEFT_OFFSET,
        minTop: TOP_OFFSET,
        initialPosition: getInitialPosition()
    });

    // Clear auto-advance timer
    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // Start auto-advance timer for icon-only steps
    const startTimer = useCallback(() => {
        clearTimer();
        if (isPlaying && !hasVideo(step)) {
            timerRef.current = setTimeout(() => {
                const nextStep = (currentStep + 1) % steps.length;
                goTo(nextStep);
            }, autoAdvanceDelay);
        }
    }, [isPlaying, step, currentStep, steps.length, goTo, autoAdvanceDelay, clearTimer]);

    // Auto-advance effect for icon-only steps
    useEffect(() => {
        if ((standalone && standaloneIsOpen) || (!standalone && !inline)) {
            startTimer();
        }
        return clearTimer;
    }, [standalone, standaloneIsOpen, inline, currentStep, startTimer, clearTimer]);

    // Reset to step 0 on mount for floating mode (context-based only)
    useEffect(() => {
        if (!standalone && !inline && currentStep !== 0) {
            goTo(0);
            setIsPlaying(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle standalone mode opening/closing
    useEffect(() => {
        if (standalone && standaloneIsOpen) {
            setLocalStep(0);
            setIsPlaying(true);
            shouldAutoPlayRef.current = true;
            // Reset position when opening
            if (initialPosition === "bottom-right") {
                setPosition(getBottomRightPosition());
            } else if (typeof initialPosition === "object") {
                // Clamp initialPosition to viewport bounds using shared utility
                setPosition(
                    clampPositionToViewport({
                        position: initialPosition,
                        cardWidth: DEFAULT_CARD_WIDTH,
                        cardHeight: CARD_HEIGHT,
                        minLeft: LEFT_OFFSET,
                        minTop: TOP_OFFSET,
                        minBottom: LEFT_OFFSET
                    })
                );
            }
        }
    }, [standalone, standaloneIsOpen, setPosition, initialPosition, getBottomRightPosition]);

    const togglePlayPause = useCallback(() => {
        setIsPlaying((prev) => {
            const newIsPlaying = !prev;
            if (videoRef.current) {
                if (newIsPlaying) {
                    videoRef.current.play();
                } else {
                    videoRef.current.pause();
                }
            }
            return newIsPlaying;
        });
    }, []);

    const handleManualNavigation = useCallback(
        (newStep: number) => {
            shouldAutoPlayRef.current = true;
            setIsPlaying(true);
            goTo(newStep);
        },
        [goTo]
    );

    const handleVideoEnded = useCallback(() => {
        if (!isPlaying) return;

        if (currentStep >= steps.length - 1) {
            if (inline) {
                shouldAutoPlayRef.current = true;
                goTo(0);
            } else {
                shouldAutoPlayRef.current = false;
                goTo(0);
                setIsPlaying(false);
            }
        } else {
            shouldAutoPlayRef.current = true;
            goTo(currentStep + 1);
        }
    }, [isPlaying, currentStep, steps.length, goTo, inline]);

    // Video playback effect
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            if (shouldAutoPlayRef.current) {
                videoRef.current.play().catch(() => {
                    // Autoplay was prevented
                });
            } else {
                videoRef.current.pause();
            }
        }
    }, [currentStep]);

    // Note: Dragging and resize logic now handled by useDraggableCard hook

    useEffect(() => {
        if (inline || standalone) return;
        const wasDismissed = prevDismissedRef.current;
        if (wasDismissed && !tutorialContext.isCardDismissed) {
            setPosition(DEFAULT_INITIAL_POSITION);
            setIsPlaying(true);
            shouldAutoPlayRef.current = true;
            if (videoRef.current) {
                videoRef.current.currentTime = 0;
                videoRef.current.play();
            }
        }
        prevDismissedRef.current = tutorialContext.isCardDismissed;
    }, [inline, standalone, tutorialContext.isCardDismissed, setPosition]);

    useKeyboardShortcut({
        key: "k",
        handler: togglePlayPause,
        disabled: inline || (standalone ? !standaloneIsOpen : tutorialContext.isCardDismissed)
    });

    const handleDismiss = standalone ? (onClose ?? (() => {})) : tutorialContext.dismissCard;

    // Media section - video or icon
    const mediaSection = hasVideo(step) ? (
        <div
            className={twClassNames(
                "relative w-full flex-shrink-0 overflow-hidden rounded-t-lg bg-gray-100",
                inline ? "h-48" : "h-56"
            )}
        >
            <video
                ref={videoRef}
                src={step.videoSrc}
                className={twClassNames(
                    "absolute inset-0 h-full w-full",
                    inline ? "object-contain" : "object-cover"
                )}
                muted
                playsInline
                autoPlay={autoPlay ?? inline}
                controls={false}
                onEnded={handleVideoEnded}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-gradient-to-t from-white to-white/0" />
            {!inline && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white/50 to-white/0" />
            )}
        </div>
    ) : (
        <div
            className={twClassNames(
                "relative flex w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-t-lg bg-gradient-to-br from-purple-50 to-purple-100",
                inline ? "h-48" : "h-56"
            )}
        >
            <AnimatePresence mode="wait">
                <motion.i
                    key={currentStep}
                    className={twClassNames(step.icon, "text-7xl text-purple-500")}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                />
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white/50 to-white/0" />
        </div>
    );

    const textSection = (
        <div className={twClassNames("flex flex-col", inline ? "px-3" : "flex-1 px-4")}>
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="flex flex-col"
                >
                    <motion.div
                        className="flex items-center justify-between gap-2"
                        variants={{
                            hidden: { opacity: 0, y: 8, filter: "blur(5px)" },
                            visible: { opacity: 1, y: 0, filter: "blur(0px)" }
                        }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <h3 className="text-sm font-semibold text-primary">{step.title}</h3>
                        <i className={twClassNames(step.icon, "text-base text-tertiary")} />
                    </motion.div>

                    <motion.p
                        className={twClassNames("text-sm text-tertiary", inline && "text-left")}
                        variants={{
                            hidden: { opacity: 0, y: 8, filter: "blur(5px)" },
                            visible: { opacity: 1, y: 0, filter: "blur(0px)" }
                        }}
                        transition={{ duration: 0.35, ease: "easeOut", delay: 0.1 }}
                    >
                        {step.description}
                    </motion.p>
                </motion.div>
            </AnimatePresence>
        </div>
    );

    const playButton = inline ? (
        <div className="absolute bottom-0 right-1">
            {/* @ts-expect-error - Button component type issue */}
            <Button
                onClick={togglePlayPause}
                aria-label={isPlaying ? "Pause" : "Play"}
                type="ghost"
                size="xsmall"
                shape="square"
            >
                <i className={twClassNames("fas", isPlaying ? "fa-pause" : "fa-play")} />
            </Button>
        </div>
    ) : (
        <div className="absolute bottom-1 right-1">
            {/* @ts-expect-error - TooltipContainer component type issue */}
            <TooltipContainer
                title={
                    <div>
                        {isPlaying ? "Pause" : "Play"}
                        <span className="hotkey dark ml-2">K</span>
                    </div>
                }
                type="titleOnly"
                customButton={
                    <span>
                        {/* @ts-expect-error - Button component type issue */}
                        <Button
                            onClick={togglePlayPause}
                            aria-label={isPlaying ? "Pause" : "Play"}
                            type="ghost"
                            size="xsmall"
                            shape="square"
                        >
                            <i
                                className={twClassNames("fas", isPlaying ? "fa-pause" : "fa-play")}
                            />
                        </Button>
                    </span>
                }
            />
        </div>
    );

    // Inline mode render
    if (inline) {
        return (
            <div className="flex h-80 w-full flex-col gap-4 rounded-xl border bg-white p-2 pb-4 shadow-lg">
                {mediaSection}
                {textSection}
            </div>
        );
    }

    // Floating mode render (both standalone and context-based)
    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    ref={cardRef}
                    className={twClassNames(DRAGGABLE_CARD_CLASSES, "p-1")}
                    style={{
                        left: position.left,
                        top: position.top,
                        width: DEFAULT_CARD_WIDTH,
                        height: CARD_HEIGHT
                    }}
                    onMouseDown={startDrag}
                    {...DRAGGABLE_CARD_ANIMATION}
                >
                    {mediaSection}
                    {textSection}
                    <AutoAdvanceDots
                        stepsCount={steps.length}
                        index={currentStep}
                        onIndexChange={handleManualNavigation}
                    />
                    {/* @ts-expect-error - HoverDeleteButton type mismatch with JSX.Element */}
                    <HoverDeleteButton
                        onClick={handleDismiss}
                        ariaLabel="Dismiss"
                        className="-right-1 -top-1"
                    />
                    {playButton}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// Convenience export for inline usage
export function InlineTutorialCard() {
    return <TutorialCard inline />;
}
